import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getUserActivityAPI,
  getUserActivityStatesAPI,
} from "../api/UserActivityApi";

const defaultPagination = {
  size: 10,
  number: 0,
  totalElements: 0,
  totalPages: 0,
};

const initialState = {
  userActivity: {},
  userActivities: [],
  userActivityStates: [],
  userActivityPagination: defaultPagination,
  userActivityStatesPagination: defaultPagination,
  filters: {
    page: 0,
    size: 10,
    sortBy: "lastVisitAt",
    sortDir: "desc",
    year: 0,
    month: 0,
    day: 0,
    startDate: "",
    endDate: "",
  },
  statesFilters: {
    activityId: "",
    page: 0,
    size: 10,
    sortBy: "createdAt",
    sortDir: "desc",
  },
  loading: false,
  statesLoading: false,
};

const getResponse = (payload) => payload?.response ?? payload;
const getContent = (response) => response?.content ?? response?.userList?.content ?? response ?? [];
const getPage = (response) => response?.page ?? response?.userList?.page ?? defaultPagination;

export const getUserActivity = createAsyncThunk(
  "userActivity/getUserActivity",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getUserActivityAPI(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message,
      });
    }
  },
);

export const getUserActivityStates = createAsyncThunk(
  "userActivity/getUserActivityStates",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getUserActivityStatesAPI(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message,
      });
    }
  },
);

const userActivitySlice = createSlice({
  name: "userActivity",
  initialState,
  reducers: {
    setUserActivityFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    resetUserActivityFilters: (state) => {
      state.filters = initialState.filters;
    },
    setUserActivityStatesFilters: (state, action) => {
      state.statesFilters = {
        ...state.statesFilters,
        ...action.payload,
      };
    },
    resetUserActivityStatesFilters: (state) => {
      state.statesFilters = initialState.statesFilters;
    },
    clearUserActivityStates: (state) => {
      state.userActivityStates = [];
      state.userActivityStatesPagination = defaultPagination;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUserActivity.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getUserActivity.fulfilled, (state, action) => {
      const response = getResponse(action.payload);

      state.userActivity = response;
      state.userActivities = getContent(response);
      state.userActivityPagination = getPage(response);
      state.loading = false;
    });
    builder.addCase(getUserActivity.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getUserActivityStates.pending, (state) => {
      state.statesLoading = true;
    });
    builder.addCase(getUserActivityStates.fulfilled, (state, action) => {
      const response = getResponse(action.payload);

      state.userActivityStates = getContent(response);
      state.userActivityStatesPagination = getPage(response);
      state.statesLoading = false;
    });
    builder.addCase(getUserActivityStates.rejected, (state) => {
      state.statesLoading = false;
    });
  },
});

export const {
  clearUserActivityStates,
  resetUserActivityFilters,
  resetUserActivityStatesFilters,
  setUserActivityFilters,
  setUserActivityStatesFilters,
} = userActivitySlice.actions;
export default userActivitySlice.reducer;
